import { apiV1Service } from '@/utils/request'
import { unwrapApiData } from './response'

export interface OrganizationSummary {
  total_seats: number
  active_seats: number
  invited_seats: number
  available_seats: number
  ai_points_total: number
  ai_points_pool: number
  contact_points_total: number
  contact_points_pool: number
  is_expired: boolean
  end_at: string | null
}

export interface OrganizationMemberItem {
  id: string
  user_id: string
  email: string
  name?: string | null
  role: 'admin' | 'member'
  is_owner?: boolean
  ai_points_available?: number
  contact_points_available?: number
  status: 'active' | 'invited' | 'rejected' | 'removed' | 'left' | 'expired'
  invited_at: string
  invite_expires_at?: string | null
  activated_at?: string | null
  ai_points_allocated: number
  ai_points_consumed: number
  contact_points_allocated: number
  contact_points_consumed: number
}

export interface OrganizationMembersResponse {
  summary: OrganizationSummary
  members: OrganizationMemberItem[]
}

export interface PendingInvitationItem {
  id: string
  organization_id: string
  organization_name: string
  invited_by_name: string
  invited_at: string
  invite_expires_at: string
}

export interface OrganizationPointsLedgerItem {
  id: string
  organization_id: string
  member_user_id?: string | null
  operator_user_id: string
  points_type: 'ai_match' | 'contact'
  action: string
  points: number
  pool_balance_after: number
  member_allocated_after?: number | null
  description: string
  context?: Record<string, unknown> | null
  created_at: string
}

export interface OpenOrganizationPayload {
  organization_id: string
  name: string
  admin_email: string
  seat_count: number
  start_at: string
  end_at: string
  ai_points_total: number
  contact_points_total: number
}

export interface AdjustPointsPayload {
  points_type: 'ai_match' | 'contact'
  mode: 'add' | 'deduct' | 'set'
  amount: number
  reason?: string
}

export const organizationApi = {
  // Platform Admin
  async openOrganization(payload: OpenOrganizationPayload) {
    const res = await apiV1Service.post<any>('/admin/organization', payload)
    return unwrapApiData(res)
  },

  async listOrganizations(params?: { page?: number; limit?: number; keyword?: string }) {
    const res = await apiV1Service.get<any>('/admin/organization', { params })
    return unwrapApiData(res)
  },

  async getOrganizationDetail(id: string) {
    const res = await apiV1Service.get<any>(`/admin/organization/${id}`)
    return unwrapApiData(res)
  },

  async transferAdmin(id: string, newAdminEmail: string) {
    const res = await apiV1Service.post<any>(`/admin/organization/${id}/transfer-admin`, {
      new_admin_email: newAdminEmail,
    })
    return unwrapApiData(res)
  },

  async updateOrganization(id: string, payload: { name?: string; status?: string }) {
    const res = await apiV1Service.patch<any>(`/admin/organization/${id}`, payload)
    return unwrapApiData(res)
  },

  async adjustPoints(id: string, payload: AdjustPointsPayload) {
    const res = await apiV1Service.post<any>(`/admin/organization/${id}/adjust-points`, payload)
    return unwrapApiData(res)
  },

  async deleteOrganization(id: string) {
    const res = await apiV1Service.delete<any>(`/admin/organization/${id}`)
    return unwrapApiData(res)
  },


  // Enterprise & Member
  async getCurrentOrganization() {
    const res = await apiV1Service.get<any>('/organization/current')
    return unwrapApiData(res)
  },

  async getOrganizationMembers(): Promise<OrganizationMembersResponse> {
    const res = await apiV1Service.get<any>('/organization/members')
    return unwrapApiData<OrganizationMembersResponse>(res)
  },

  async inviteMember(email: string) {
    const res = await apiV1Service.post<any>('/organization/invite', { email })
    return unwrapApiData(res)
  },

  async cancelInvitation(memberId: string) {
    const res = await apiV1Service.post<any>(`/organization/members/${memberId}/cancel-invite`)
    return unwrapApiData(res)
  },

  async adjustMemberQuota(
    memberId: string,
    payload: { points_type: 'ai_match' | 'contact'; target_amount: number },
  ) {
    const res = await apiV1Service.post<any>(`/organization/members/${memberId}/quota`, payload)
    return unwrapApiData(res)
  },

  async removeMember(memberId: string) {
    const res = await apiV1Service.post<any>(`/organization/members/${memberId}/remove`)
    return unwrapApiData(res)
  },

  async getOrganizationLedger(params?: { page?: number; limit?: number }) {
    const res = await apiV1Service.get<any>('/organization/ledger', { params })
    return unwrapApiData(res)
  },

  // Invitations
  async getPendingInvitations(): Promise<PendingInvitationItem[]> {
    const res = await apiV1Service.get<any>('/organization/invitations/pending')
    return unwrapApiData<PendingInvitationItem[]>(res) || []
  },

  async respondToInvitation(memberId: string, accept: boolean) {
    const res = await apiV1Service.post<any>(`/organization/invitations/${memberId}/respond`, {
      accept,
    })
    return unwrapApiData(res)
  },

  async leaveOrganization() {
    const res = await apiV1Service.post<any>('/organization/leave')
    return unwrapApiData(res)
  },
}
